import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, message, Tooltip, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  createCategoryService,
  updateCategoryService,
  deleteCategoryService,
  fetchCategoriesService,
} from '../../services/CategoryService';

const { confirm } = Modal;

const CategoryTable = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form] = Form.useForm();

  const fetchCategories = useCallback(() => {
    setTableLoading(true);
    fetchCategoriesService()
      .then(data => {
        setCategories(data);
      })
      .catch(error => {
        message.error('Error fetching categories');
      })
      .finally(() => {
        setTableLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Are you sure you want to delete this category?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteCategoryService(id)
          .then(() => {
            setCategories(prevCategories => prevCategories.filter(cat => cat.id !== id));
            message.success('Category deleted successfully');
          })
          .catch(error => {
            message.error('Error deleting category');
          });
      },
      onCancel() {
        console.log('Delete canceled');
      },
    });
  };

  const handleSubmit = (values) => {
    setFormLoading(true);
    const { name, parentId } = values;

    if (editingCategory) {
      updateCategoryService(editingCategory.id, name)
        .then(updatedCategory => {
          setCategories(prevCategories =>
            prevCategories.map(cat => cat.id === editingCategory.id ? updatedCategory : cat)
          );
          message.success('Category updated successfully');
          setIsModalVisible(false);
          form.resetFields();
        })
        .catch(error => {
          message.error('Error updating category');
        })
        .finally(() => {
          setFormLoading(false);
        });
    } else {
      createCategoryService(name, parentId)
        .then(() => {
          return fetchCategoriesService();
        })
        .then(updatedCategories => {
          setCategories(updatedCategories);
          message.success('Category created successfully');
          setIsModalVisible(false);
          form.resetFields();
        })
        .catch(error => {
          message.error('Error creating category');
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  };

  const showEditModal = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({ name: category.name, parentId: category.parentId || null });
    setIsModalVisible(true);
  };

  const showCreateModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => showDeleteConfirm(record.id)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" onClick={showCreateModal} style={{ marginBottom: 16 }}>
        Create Category
      </Button>

      <Spin spinning={tableLoading}>
        <Table columns={columns} dataSource={categories} rowKey="id" />
      </Spin>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Create Category'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={formLoading}
      >
        <Form form={form} onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please input the category name!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CategoryTable;